// src/components/Dashboard/LicenseList.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, deleteDoc, collection, getDocs } from "firebase/firestore";
import { db, storage } from "../../firebase/firebase";
import { Button, Table, Container, Form } from "react-bootstrap";
import { ArrowUp, ArrowDown } from 'react-bootstrap-icons';
import './LicenseList.css';
import * as XLSX from 'xlsx'; // Import the xlsx library
import { ref, deleteObject } from "firebase/storage";

const LicenseList = () => {
  const [licenses, setLicenses] = useState([]);
  const [employees, setEmployees] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({ software_name: '', software_company: '' });
  const [softwareNames, setSoftwareNames] = useState([]);
  const [softwareCompanies, setSoftwareCompanies] = useState([]);
  const [showFilter, setShowFilter] = useState(false); // State to manage filter form visibility

  // Fetch licenses from Firestore
  useEffect(() => {
    const fetchLicenses = async () => {
      const querySnapshot = await getDocs(collection(db, "licenses"));
      const licensesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLicenses(licensesData);

      // Extract unique software names and companies
      const uniqueSoftwareNames = [...new Set(licensesData.map((license) => license.software_name))];
      const uniqueSoftwareCompanies = [...new Set(licensesData.map((license) => license.software_company))];
      setSoftwareNames(uniqueSoftwareNames);
      setSoftwareCompanies(uniqueSoftwareCompanies);
    };

    const fetchEmployees = async () => {
      const querySnapshot = await getDocs(collection(db, "employees"));
      const employeesData = {};
      querySnapshot.forEach((doc) => {
        employeesData[doc.id] = doc.data().full_name;
      });
      setEmployees(employeesData);
    };

    fetchLicenses();
    fetchEmployees();
  }, []);

  const handleDelete = async (id) => {
    try {
      // Fetch the license document before deleting it
      const licenseDoc = licenses.find((license) => license.id === id);
      if (!licenseDoc) {
        alert("License not found!");
        return;
      }

      // Extract quote and receipt URLs
      const { quote_url, receipt_url } = licenseDoc;

      // Delete the license from Firestore
      await deleteDoc(doc(db, "licenses", id));

      // Delete associated files from Firebase Storage
      if (quote_url) {
        const quoteRef = ref(storage, quote_url);
        await deleteObject(quoteRef).catch((error) => console.error("Error deleting quote:", error));
      }

      if (receipt_url) {
        const receiptRef = ref(storage, receipt_url);
        await deleteObject(receiptRef).catch((error) => console.error("Error deleting receipt:", error));
      }

      alert("License and associated files deleted successfully!");

      // Update state to remove the deleted license
      setLicenses(licenses.filter((license) => license.id !== id));

    } catch (error) {
      console.error("Error deleting license:", error);
      alert("Failed to delete license. Please try again.");
    }
  };

  // const calculateDaysRemaining = (expiryDate) => {
  //   const currentDate = new Date();
  //   const expirationDate = new Date(expiryDate);
  //   const timeDifference = expirationDate - currentDate;
  //   return Math.ceil(timeDifference / (1000 * 3600 * 24)); // Convert ms to days
  // };

  // Function to handle exporting the table to Excel
  const exportToExcel = () => {
    const data = licenses.map((license) => ({
      "מזהה רשיון": license.license_id,
      "שם התוכנה": license.software_name,
      "חברה": license.software_company || "N/A",
      "סוג רשיון": license.license_type === "subscription" ? "מנוי" : "רכישה מלאה",
      "תאריך תפוגה": license.expiry_date || "N/A",
      "מחיר לשנה למושב": license.price_per_year || "N/A",
      "מחיר למושב": license.price_per_seat || "N/A",
      "מחיר מלא": license.full_price || "N/A",
      "תאריך רכישה": license.purchase_date || "N/A",
      "סטטוס": license.status,
      "מספר מושבים": license.seats,
      "מספר מושבים תפוסים": license.seat_assignment ? license.seat_assignment.length : 0,
      "עובדים משויכים": license.seat_assignment
        ? license.seat_assignment.map((empId) => employees[empId] || "Unknown").join(", ")
        : "No Assignments",
      "תמיכה": license.support
        ? `${license.support.fullName}, ${license.support.email}, ${license.support.phone}`
        : "No Support",
      "חשבונית": license.receipt_url ? "הורד חשבונית" : "לא הועלתה",
      "הצעת מחיר": license.quote_url ? "הורד הצעת מחיר" : "לא הועלתה",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Licenses");

    // Export to Excel file
    XLSX.writeFile(wb, "Licenses_List.xlsx");
  };

  const sortedLicenses = [...licenses]
    .filter((license) => {
      return (
        (filterConfig.software_name === '' || license.software_name.toLowerCase().includes(filterConfig.software_name.toLowerCase())) &&
        (filterConfig.software_company === '' || license.software_company.toLowerCase().includes(filterConfig.software_company.toLowerCase()))
      );
    })
    .sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterConfig((prevConfig) => ({
      ...prevConfig,
      [name]: value,
    }));
  };

  const filteredSoftwareNames = filterConfig.software_company
    ? [...new Set(licenses
        .filter((license) => license.software_company === filterConfig.software_company)
        .map((license) => license.software_name))]
    : softwareNames;

  const filteredSoftwareCompanies = filterConfig.software_name
    ? [...new Set(licenses
        .filter((license) => license.software_name === filterConfig.software_name)
        .map((license) => license.software_company))]
    : softwareCompanies;

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const clearFilter = () => {
    setFilterConfig({ software_name: '', software_company: '' });
    setShowFilter(false);
  };

  return (
    <Container className="license-list">
      <h2 className="mb-3">רשיונות</h2>
      <div className="top-list-buttons">
        {!showFilter && (
          <>
            <Link to="/dashboard/add-license" className="btn btn-primary mb-3">
              הוסף רשיון חדש
            </Link>
            <Button variant="success" onClick={exportToExcel} className="mb-3">
              ייצוא ל-Excel
            </Button>
            <Button variant="info" onClick={toggleFilter} className="mb-3">
              סינון
            </Button>
          </>
        )}
        {showFilter && (
          <Button variant="secondary" onClick={clearFilter} className="mb-3">
            ביטול
          </Button>
        )}
      </div>
      {showFilter && (
        <Form className="mb-3 license-list-filter">
          <Form.Group controlId="filterSoftwareName">
            <Form.Label>סנן לפי שם תוכנה</Form.Label>
            <Form.Control
              as="select"
              name="software_name"
              value={filterConfig.software_name}
              onChange={handleFilterChange}
            >
              <option value="">בחר שם תוכנה</option>
              {filteredSoftwareNames.map((name, index) => (
                <option key={index} value={name}>{name}</option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="filterSoftwareCompany">
            <Form.Label>סנן לפי שם חברה</Form.Label>
            <Form.Control
              as="select"
              name="software_company"
              value={filterConfig.software_company}
              onChange={handleFilterChange}
            >
              <option value="">בחר שם חברה</option>
              {filteredSoftwareCompanies.map((company, index) => (
                <option key={index} value={company}>{company}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Form>
      )}
      <div className="license-list-table">
        <Table striped bordered hover>
          <thead>
            <tr className="license-list-header">
              <th onClick={() => requestSort('license_id')}>
                מזהה רשיון {sortConfig.key === 'license_id' && (sortConfig.direction === 'asc' ? <ArrowUp /> : <ArrowDown />)}
              </th>
              <th onClick={() => requestSort('software_name')}>
                שם התוכנה {sortConfig.key === 'software_name' && (sortConfig.direction === 'asc' ? <ArrowUp /> : <ArrowDown />)}
              </th>
              <th onClick={() => requestSort('software_company')}>
                חברה {sortConfig.key === 'software_company' && (sortConfig.direction === 'asc' ? <ArrowUp /> : <ArrowDown />)}
              </th>
              <th>סוג רשיון</th>
              <th>תאריך תפוגה</th>
              <th>מחיר לשנה למושב</th>
              <th>מחיר למושב</th>
              <th>מחיר מלא</th>
              <th>תאריך רכישה</th>
              <th>סטטוס</th>
              <th>מספר מושבים</th>
              <th>מספר מושבים תפוסים</th>
              <th>עובדים משויכים</th>
              <th>תמיכה</th>
              <th>חשבונית</th>
              <th>הצעת מחיר</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
          {sortedLicenses.map((license) => {
  const availableSeats = license.seats - (license.seat_assignment ? license.seat_assignment.length : 0);

  // Calculate price per seat
  const pricePerSeat = license.full_price && license.seats
    ? (license.full_price / license.seats).toFixed(2)
    : "N/A";

  // Calculate price per year (only for subscriptions)
  let pricePerYear = "N/A";
  if (license.license_type === "subscription" && license.full_price && license.expiry_date && license.purchase_date) {
    const purchaseDate = new Date(license.purchase_date);
    const expiryDate = new Date(license.expiry_date);
    const timeDifference = expiryDate - purchaseDate;
    const years = timeDifference / (1000 * 3600 * 24 * 365); // Convert milliseconds to years
    pricePerYear = years > 0 ? (license.full_price / years).toFixed(2) : "N/A";
  }

  // Determine status based on license type and expiry date
  const currentDate = new Date();
  const expiryDate = license.expiry_date ? new Date(license.expiry_date) : null;
  const status =
    license.license_type === "fully_paid"
      ? "Active"
      : expiryDate && expiryDate > currentDate
      ? "Active"
      : "Expired";

  return (
    <tr key={license.id}>
      <td>{license.license_id}</td>
      <td>{license.software_name}</td>
      <td>{license.software_company || "N/A"}</td>
      <td>{license.license_type === "subscription" ? "מנוי" : "רכישה מלאה"}</td>
      <td>{license.expiry_date || "N/A"}</td>
      <td>{pricePerYear}</td>
      <td>{pricePerSeat}</td>
      <td>{license.full_price || "N/A"}</td>
      <td>{license.purchase_date || "N/A"}</td>
      <td>{status}</td>
      <td>{license.seats}</td>
      <td className={availableSeats > 0 ? "text-danger fw-bold" : "text-success fw-bold"}>
        {license.seat_assignment ? license.seat_assignment.length : 0}
      </td>
      <td>
        {license.seat_assignment ? (
          <ul style={{ listStyleType: "disc", paddingRight: "20px" }}>
            {license.seat_assignment.map((empId, index) => (
              <li key={index}>{employees[empId] || "Unknown"}</li>
            ))}
          </ul>
        ) : (
          "No Assignments"
        )}
      </td>
      <td>
        {license.support
          ? `${license.support.fullName}\n${license.support.email}\n${license.support.phone}`
          : "No Support"}
      </td>
      <td>
        {license.receipt_url ? (
          <a href={license.receipt_url} target="_blank" rel="noopener noreferrer">
            הורד חשבונית
          </a>
        ) : (
          "לא הועלתה"
        )}
      </td>
      <td>
        {license.quote_url ? (
          <a href={license.quote_url} target="_blank" rel="noopener noreferrer">
            הורד הצעת מחיר
          </a>
        ) : (
          "לא הועלתה"
        )}
      </td>
      <td className="table-actions">
        <Link to={`/dashboard/edit-license/${license.id}`} className="btn btn-warning btn-sm me-2">
          עריכה
        </Link>
        <Button variant="danger" size="sm" onClick={() => handleDelete(license.id)}>
          מחיקה
        </Button>
      </td>
    </tr>
  );
})}
          </tbody>
        </Table>
      </div>
    </Container>
  );
};

export default LicenseList;
